<script setup lang="ts" generic="T extends Record<string, any>">
import { computed } from 'vue'

type ChipTheme = 'purple' | 'blue'

type ChipThemeClasses = {
  chip: string
  selected: string
  unselected: string
  icon: string
}

const CHIP_THEME_CLASSES: Record<ChipTheme, ChipThemeClasses> = {
  purple: {
    chip: 'gap-2 border pr-4 py-2 text-base',
    selected: 'chip-selected pl-2 bg-purple-100 border-purple-300 text-gray-900 hover:bg-purple-200',
    unselected: 'pl-4 bg-gray-50 border-gray-600 text-gray-900 hover:bg-gray-300',
    icon: 'text-purple-300',
  },
  blue: {
    chip: 'chip-blue gap-2 border py-2 text-md text-gray-900',
    selected: 'chip-selected pl-2 pr-4 bg-blue-200 border-blue-400',
    unselected: 'px-6 bg-gray-200 border-transparent hover:bg-gray-300',
    icon: 'text-blue-400',
  },
}

const props = withDefaults(defineProps<{
  items: T[]
  modelValue: T[]
  itemKey?: string
  itemLabel?: string
  showIcon?: boolean
  gbTheme?: ChipTheme
  groupLabelledby?: string
  invalid?: boolean
  errorMessageId?: string
}>(), {
  itemKey: 'value',
  itemLabel: 'label',
  showIcon: true,
  gbTheme: 'purple'
})

const emit = defineEmits<{
  'update:modelValue': [value: T[]]
}>()

const isSelected = (item: T): boolean => {
  return props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey])
}

const toggleSelection = (item: T) => {
  let newSelection: T[]
  
  if (isSelected(item)) {
    newSelection = props.modelValue.filter(selected => selected[props.itemKey] !== item[props.itemKey])
  } else {
    newSelection = [...props.modelValue, item]
  }
  
  emit('update:modelValue', newSelection)
}

const themeClasses = computed(() => CHIP_THEME_CLASSES[props.gbTheme])

const getChipClasses = (item: T) => {
  const stateClasses = isSelected(item) ? themeClasses.value.selected : themeClasses.value.unselected
  return `inline-flex items-center rounded-lg font-normal cursor-pointer transition-all select-none ${themeClasses.value.chip} ${stateClasses}`
}
</script>

<template>
  <div
    class="flex flex-wrap gap-2"
    :role="groupLabelledby ? 'group' : undefined"
    :aria-labelledby="groupLabelledby || undefined"
  >
    <label
      v-for="item in items"
      :key="item[itemKey]"
      :for="`chip-${item[itemKey]}`"
      :class="getChipClasses(item)"
    >
      <input
        :id="`chip-${item[itemKey]}`"
        type="checkbox"
        class="sr-only"
        :checked="isSelected(item)"
        :value="item[itemKey]"
        :aria-label="item[itemLabel]"
        :aria-invalid="invalid || undefined"
        :aria-describedby="invalid ? errorMessageId : undefined"
        @change="toggleSelection(item)"
      />
      <span
        v-if="showIcon && isSelected(item)"
        class="material-icons-round text-base"
        :class="themeClasses.icon"
        aria-hidden="true"
      >
        check
      </span>
      <span>{{ item[itemLabel] }}</span>
    </label>
  </div>
</template>

<style scoped>
label:has(:focus-visible) {
  @apply outline outline-2 outline-offset-2;
}

/* Purple theme focus - selected */
label.chip-selected:not(.chip-blue):has(:focus-visible) {
  @apply outline-purple-500;
  @apply bg-purple-200;
}

/* Default (unselected) focus */
label:not(.chip-selected):not(.chip-blue):has(:focus-visible) {
  @apply outline-gray-600;
  @apply bg-gray-300;
}

label.chip-blue:has(:focus-visible) {
  @apply outline-gray-900;
}
</style>
